import React, { useState } from 'react';
import { X, File, Search } from 'lucide-react';
import { NOTE_TEMPLATES } from './NoteTemplates';

const TemplateCard = ({ template, onSelect, index }) => (
    <div
        onClick={() => onSelect(template.content)}
        className="bg-white/5 border border-white/10 rounded-xl p-6 cursor-pointer group transition-all hover:shadow-lg hover:border-[#ff4d4d]/50 hover:-translate-y-1 animate-fadeIn"
        style={{ animationDelay: `${(index + 1) * 50}ms` }}
    >
        <div className="flex items-start gap-4">
            <div className="p-3 bg-gradient-to-br from-[#ff4d4d] to-[#ff6b6b] rounded-lg shadow-lg shadow-red-900/20 text-white">
                <template.icon size={24} />
            </div>
            <div>
                <h3 className="text-base font-bold text-white mb-1 group-hover:text-[#ff4d4d] transition-colors">
                    {template.title}
                </h3>
                <p className="text-sm text-white/50 leading-snug">
                    {template.description}
                </p>
            </div>
        </div>
    </div>
);

const TemplatePicker = ({ onSelect, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredTemplates = NOTE_TEMPLATES.filter(template =>
        template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn"
            onClick={onClose}
        >
            <div
                className="w-full max-w-4xl max-h-[80vh] flex flex-col bg-[#0d1424] border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-slideUp"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-6 border-b border-white/10">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-xl font-bold text-white">Create a new note</h2>
                            <p className="text-white/50 text-sm mt-1">
                                Start from a template or a blank page.
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Search */}
                    <div className="relative mt-4">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                        <input
                            type="text"
                            placeholder="Search templates..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 focus:border-[#ff4d4d] transition"
                        />
                    </div>
                </div>

                {/* Templates Grid */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Blank Note */}
                        <div
                            onClick={() => onSelect('')}
                            className="h-full bg-white/5 border border-white/10 rounded-xl p-6 cursor-pointer group transition-all hover:shadow-lg hover:border-sky-500/50 hover:-translate-y-1 flex items-center animate-fadeIn"
                        >
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-gradient-to-br from-sky-500 to-blue-600 rounded-lg shadow-lg shadow-sky-900/20 text-white">
                                    <File size={24} />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-white mb-1 group-hover:text-sky-500 transition-colors">
                                        Blank Note
                                    </h3>
                                    <p className="text-sm text-white/50 leading-snug">
                                        Start with a fresh, empty page.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {filteredTemplates.map((template, index) => (
                            <TemplateCard key={template.id} template={template} onSelect={onSelect} index={index} />
                        ))}
                    </div>

                    {filteredTemplates.length === 0 && searchTerm && (
                        <div className="text-center py-12">
                            <p className="text-white/50">No templates found for "{searchTerm}".</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TemplatePicker;
